/**
 * Product page components.
 *
 * Three custom elements, all scoped to the main product section:
 *
 *   <product-gallery>  the mobile photo carousel's arrows and announcements,
 *                      and showImage() for the variant picker
 *   <product-tabs>     the tab strip / accordion state
 *   <variant-picker>   the "Select {option}" pop-ups and the selected variant
 *
 * WHY THIS IS A REAL ASSET FILE AND NOT A {% javascript %} BLOCK: section JS was
 * measured NOT running at all on this store, and it cost three rounds of
 * debugging on the header. Anything we depend on goes in assets/ with an
 * explicit `<script defer>` in layout/theme.liquid, where the load order is ours
 * to see and a failure shows up in the network panel.
 *
 * WHERE THE WORK IS SPLIT. Layout, the two presentations of the tab group, the
 * collapse animation and every string live in the Liquid — see the comment
 * blocks in sections/product.liquid and snippets/product-tabs.liquid. This file
 * owns state, and the things state drags along: ARIA, `inert`, and the live
 * regions.
 *
 * BOTH COMPONENTS DEGRADE RATHER THAN BREAK. Without this script the gallery is
 * still swipeable (native scroll-snap) with its arrows hidden, and the first tab
 * is open with the rest closed and `inert` — which is a correct, if static,
 * page. Nothing here is required to read the product.
 */

/**
 * Mobile photo carousel.
 *
 * The track is a native horizontal scroll-snap container, so a swipe needs no
 * JavaScript at all. This adds the two arrows, which are rendered `hidden` and
 * revealed here — an arrow that cannot scroll anything is worse than no arrow.
 *
 * The arrows are `lg:hidden` in CSS, so nothing below guards on the breakpoint:
 * from `lg` up the track is a vertical stack, the buttons have no box, and the
 * listeners are simply never fired.
 *
 * THIS LOGIC NOW HAS A SECOND CONSUMER: <film-strip> in assets/film-strip.js is a
 * deliberate copy of it, not a shared base — copying literals is the theme's
 * recorded precedent and two consumers is a coincidence. A THIRD would justify
 * extracting one; if you are about to write it, extract instead of copying, and
 * fix both of these at the same time.
 */
if (!customElements.get('product-gallery')) {
  customElements.define(
    'product-gallery',
    class ProductGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-gallery-track]');
        this.prevButton = this.querySelector('[data-gallery-prev]');
        this.nextButton = this.querySelector('[data-gallery-next]');
        this.statusRegion = this.querySelector('[data-gallery-status]');

        /**
         * A single-image product renders no arrows at all, so there is nothing
         * to wire and nothing to fail. Leave quietly rather than warning.
         */
        if (!this.track || !this.prevButton || !this.nextButton) return;

        this.slides = Array.from(this.track.querySelectorAll('[data-gallery-slide]'));
        if (this.slides.length < 2) return;

        this.handlePrev = this.handlePrev.bind(this);
        this.handleNext = this.handleNext.bind(this);
        this.handleScroll = this.handleScroll.bind(this);

        this.prevButton.addEventListener('click', this.handlePrev);
        this.nextButton.addEventListener('click', this.handleNext);
        this.track.addEventListener('scroll', this.handleScroll, { passive: true });

        this.prevButton.hidden = false;
        this.nextButton.hidden = false;
        this.syncButtons();
      }

      disconnectedCallback() {
        this.prevButton?.removeEventListener('click', this.handlePrev);
        this.nextButton?.removeEventListener('click', this.handleNext);
        this.track?.removeEventListener('scroll', this.handleScroll);
      }

      /**
       * Which slide is currently showing.
       *
       * Derived from scroll position rather than stored in a field, because the
       * visitor can swipe, and a stored index would immediately be a lie. The
       * DOM is the state; this reads it.
       */
      get currentIndex() {
        const width = this.slides[0].offsetWidth;
        if (!width) return 0;
        return Math.round(this.track.scrollLeft / width);
      }

      handlePrev() {
        this.scrollToIndex(this.currentIndex - 1);
      }

      handleNext() {
        this.scrollToIndex(this.currentIndex + 1);
      }

      scrollToIndex(index) {
        const target = Math.min(Math.max(index, 0), this.slides.length - 1);
        const slide = this.slides[target];
        if (!slide) return;

        /**
         * `prefers-reduced-motion` gets an instant jump, not a smooth glide.
         * The destination is identical either way — only the tween is dropped,
         * which is the rule the rest of the theme follows.
         */
        const reduced = window.themeScroll
          ? window.themeScroll.prefersReducedMotion
          : window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.track.scrollTo({
          left: slide.offsetLeft - this.track.offsetLeft,
          behavior: reduced ? 'auto' : 'smooth',
        });

        this.announce(target);
      }

      handleScroll() {
        this.syncButtons();
      }

      /**
       * Disabled rather than hidden at the ends. A control that vanishes moves
       * everything around it and gives no clue why; a disabled one keeps its
       * place and announces its own state.
       */
      syncButtons() {
        const index = this.currentIndex;
        this.prevButton.disabled = index <= 0;
        this.nextButton.disabled = index >= this.slides.length - 1;
      }

      /**
       * Announced on arrow presses only, never on scroll — a swipe fires dozens
       * of scroll events and would flood a screen reader with position updates
       * the visitor did not ask for.
       *
       * The string is built in Liquid and read out of a <template>, so
       * pluralisation and translation stay where they belong. See the same
       * pattern in assets/cart-drawer.js.
       */
      /**
       * Bring one photograph into view, by its Shopify image id. Called by
       * <variant-picker> on hover and on selection.
       *
       * PUBLIC, AND DELIBERATELY INDEPENDENT OF connectedCallback's wiring. A
       * single-photo product never binds the arrows (connectedCallback leaves
       * early), but a picker can still ask for its one photo — so this looks
       * its elements up itself rather than trusting fields that may not exist.
       *
       * Two presentations, two scrolls. From `lg` the photographs are the PAGE,
       * stacked, so the page scrolls — through the shared Lenis instance, never
       * a second scroller. The content column is sticky, so the pop-up that
       * asked stays exactly where it is while the photographs move behind it.
       * Below `lg` they are a horizontal track, and only the track moves; the
       * page, and the pop-up the visitor is using, stay put.
       *
       * Silently does nothing for a missing id or an unknown image — a variant
       * with no photograph assigned is a normal state, not an error.
       */
      showImage(imageId) {
        if (!imageId) return;

        const track = this.querySelector('[data-gallery-track]');
        const slide = this.querySelector(
          `[data-gallery-slide][data-image-id="${CSS.escape(String(imageId))}"]`,
        );
        if (!track || !slide) return;

        const reduced = window.themeScroll
          ? window.themeScroll.prefersReducedMotion
          : window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (window.matchMedia('(min-width: 64rem)').matches) {
          /**
           * Offset 0, not the header height: the first photograph already sits
           * under the overlaid header at the top of the page, so aligning a
           * slide's top with the viewport's reproduces exactly that framing.
           */
          if (window.themeScroll) {
            window.themeScroll.scrollTo(slide, { offset: 0, immediate: reduced });
          } else {
            slide.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
          }
          return;
        }

        track.scrollTo({
          left: slide.offsetLeft - track.offsetLeft,
          behavior: reduced ? 'auto' : 'smooth',
        });
      }

      announce(index) {
        if (!this.statusRegion) return;
        const template = this.querySelector('[data-gallery-status-template]');
        if (!template) return;

        this.statusRegion.textContent = template.content.textContent
          .replace('__current__', String(index + 1))
          .replace('__total__', String(this.slides.length));
      }
    },
  );
}

/**
 * Product tabs.
 *
 * A DISCLOSURE SET, NOT AN ARIA TABLIST, and the same pattern at both
 * breakpoints. Desktop looks like tabs because of where CSS grid puts the
 * buttons, not because the roles change — swapping roles on a media query means
 * two ARIA contracts to keep correct and a resize that can strand a screen
 * reader mid-pattern.
 *
 * ONE OPEN AT A TIME, always. Opening a tab closes whichever was open, which is
 * true of the tab strip and the accordion alike, so there is one rule rather
 * than one per breakpoint.
 *
 * `data-open` and `inert` are toggled TOGETHER on the panel. The first drives
 * the collapse animation and the desktop `display: none`; the second is what
 * actually removes a collapsed panel's links from the tab order and from the
 * accessibility tree. Off-screen is not hidden.
 */
if (!customElements.get('product-tabs')) {
  customElements.define(
    'product-tabs',
    class ProductTabs extends HTMLElement {
      connectedCallback() {
        this.triggers = Array.from(this.querySelectorAll('[data-tab-trigger]'));
        this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));

        /**
         * Fail loudly and leave the rendered state alone. The Liquid ships with
         * the first tab open and the rest closed AND `inert`, so broken wiring
         * is a page that does not toggle rather than a set of collapsed panels
         * a screen reader can still walk into.
         */
        if (this.triggers.length === 0 || this.triggers.length !== this.panels.length) {
          console.warn('[product-tabs] markup is incomplete; tabs disabled');
          return;
        }

        this.handleClick = this.handleClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);

        this.triggers.forEach((trigger) => {
          trigger.addEventListener('click', this.handleClick);
          trigger.addEventListener('keydown', this.handleKeydown);
        });
      }

      disconnectedCallback() {
        this.triggers?.forEach((trigger) => {
          trigger.removeEventListener('click', this.handleClick);
          trigger.removeEventListener('keydown', this.handleKeydown);
        });
      }

      handleClick(event) {
        const index = this.triggers.indexOf(event.currentTarget);
        if (index === -1) return;

        /**
         * Re-pressing the open tab collapses it on mobile, where an accordion
         * that cannot be closed is surprising — but NOT from `lg` up, where the
         * strip would be left pointing at nothing and the column would jump.
         */
        const isOpen = event.currentTarget.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          if (window.matchMedia('(min-width: 64rem)').matches) return;
          this.open(-1);
          return;
        }

        this.open(index);
      }

      /**
       * Arrow keys walk the strip, Home and End jump to its ends.
       *
       * Focus moves without opening. Activation stays on Enter and Space —
       * the browser already fires `click` for both on a real <button>, so
       * there is nothing here to handle for them.
       */
      handleKeydown(event) {
        const index = this.triggers.indexOf(event.currentTarget);
        if (index === -1) return;

        let target = null;
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            target = (index + 1) % this.triggers.length;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            target = (index - 1 + this.triggers.length) % this.triggers.length;
            break;
          case 'Home':
            target = 0;
            break;
          case 'End':
            target = this.triggers.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        this.triggers[target].focus();
      }

      /** Pass -1 to close everything. */
      open(index) {
        this.triggers.forEach((trigger, i) => {
          const panel = this.panels[i];
          const isOpen = i === index;

          trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

          if (isOpen) {
            panel.setAttribute('data-open', '');
            panel.removeAttribute('inert');
          } else {
            panel.removeAttribute('data-open');
            panel.setAttribute('inert', '');
          }
        });
      }
    },
  );
}

/**
 * Variant picker.
 *
 * One "Select {option}" trigger per product option, each opening a pop-up row
 * of swatches. Choosing a swatch resolves the selection to a variant and pushes
 * it into the page: the form's hidden `id`, the price line, the buy button's
 * state, the URL, and the gallery.
 *
 * A DISCLOSURE, NOT A MENU OR A LISTBOX. The trigger is a button with
 * `aria-expanded`; the swatches are ordinary buttons with `aria-pressed`. That
 * keeps Tab working inside the pop-up (a menu would trap it behind arrow keys)
 * and needs no roving-tabindex bookkeeping. Arrow keys are offered on top, not
 * instead.
 *
 * WHAT CLOSES IT: choosing a swatch, Escape, a pointer press anywhere outside,
 * and keyboard focus leaving it. Only one pop-up is open at a time.
 *
 * HOVER PREVIEW, DESKTOP MICE ONLY. Hovering a swatch shows that variant's
 * photograph in the gallery; leaving the pop-up returns the gallery to the
 * SELECTED variant's photograph, so a preview never masquerades as a choice.
 * It is debounced, because on desktop the preview scrolls the whole page and a
 * sweep across eight swatches must not become eight scrolls. Touch has no
 * hover: a tap selects, which scrolls the carousel to the choice.
 *
 * ALL TEXT COMES FROM THE LIQUID — button labels in data attributes on the
 * button, announcement and "selected" strings as templates with a `__value__`
 * placeholder. Nothing user-facing is written here.
 *
 * DEGRADES TO THE SERVER'S CHOICE. Without this script the pop-ups never open
 * and the form posts the variant Liquid rendered (`?variant=` still works).
 */
if (!customElements.get('variant-picker')) {
  customElements.define(
    'variant-picker',
    class VariantPicker extends HTMLElement {
      /** How long the pointer must rest on a swatch before the gallery moves. */
      static HOVER_DELAY = 120;

      connectedCallback() {
        const dataElement = this.querySelector('[data-variant-data]');
        this.groups = Array.from(this.querySelectorAll('[data-option-group]'));
        this.buyArea = this.closest('[data-buy-area]');
        this.idInput = this.buyArea?.querySelector('[data-variant-id-input]');
        this.priceElement = this.buyArea?.querySelector('[data-product-price]');
        this.addButton = this.buyArea?.querySelector('[data-add-button]');
        this.statusRegion = this.querySelector('[data-variant-status]');

        /**
         * Fail loudly and leave the server-rendered state alone. That state is
         * a correct page: the right variant, price and button, just no pop-up.
         */
        if (!dataElement || this.groups.length === 0 || !this.idInput) {
          console.warn('[variant-picker] markup is incomplete; picker disabled');
          return;
        }

        try {
          this.variants = JSON.parse(dataElement.textContent);
        } catch (error) {
          console.warn('[variant-picker] variant data is not valid JSON; picker disabled', error);
          return;
        }

        /** One entry per option, in option order — the same order as variant.options. */
        this.selected = this.groups.map(
          (group) =>
            group.querySelector('[data-option-value][aria-pressed="true"]')?.dataset.optionValue ??
            null,
        );

        this.openGroup = null;
        this.hoverTimer = null;
        this.previewing = false;

        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleValueClick = this.handleValueClick.bind(this);
        this.handlePointerOver = this.handlePointerOver.bind(this);
        this.handlePointerLeave = this.handlePointerLeave.bind(this);
        this.handlePanelKeydown = this.handlePanelKeydown.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);
        this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
        this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);

        this.groups.forEach((group) => {
          const trigger = group.querySelector('[data-option-trigger]');
          const panel = group.querySelector('[data-option-panel]');
          trigger?.addEventListener('click', this.handleTriggerClick);
          panel?.addEventListener('click', this.handleValueClick);
          panel?.addEventListener('pointerover', this.handlePointerOver);
          panel?.addEventListener('pointerleave', this.handlePointerLeave);
          panel?.addEventListener('keydown', this.handlePanelKeydown);
          group.addEventListener('focusout', this.handleFocusOut);
        });

        document.addEventListener('pointerdown', this.handleDocumentPointerDown);
        document.addEventListener('keydown', this.handleDocumentKeydown);

        this.bound = true;
        this.syncValues();
      }

      disconnectedCallback() {
        if (!this.bound) return;
        clearTimeout(this.hoverTimer);

        this.groups.forEach((group) => {
          const trigger = group.querySelector('[data-option-trigger]');
          const panel = group.querySelector('[data-option-panel]');
          trigger?.removeEventListener('click', this.handleTriggerClick);
          panel?.removeEventListener('click', this.handleValueClick);
          panel?.removeEventListener('pointerover', this.handlePointerOver);
          panel?.removeEventListener('pointerleave', this.handlePointerLeave);
          panel?.removeEventListener('keydown', this.handlePanelKeydown);
          group.removeEventListener('focusout', this.handleFocusOut);
        });

        document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
        document.removeEventListener('keydown', this.handleDocumentKeydown);
        this.bound = false;
      }

      /**
       * The gallery lives in the same section, not inside this element. Looked
       * up on demand rather than cached, so the theme editor re-rendering one
       * without the other cannot leave a stale reference behind.
       */
      get gallery() {
        const section = this.closest('.shopify-section') ?? document;
        return section.querySelector('product-gallery');
      }

      /** The variant matching every current selection, or undefined. */
      get currentVariant() {
        return this.findVariant(this.selected);
      }

      findVariant(selection) {
        return this.variants.find((variant) =>
          variant.options.every((value, index) => value === selection[index]),
        );
      }

      /* ---------------------------------------------------------------- open/close */

      handleTriggerClick(event) {
        const group = event.currentTarget.closest('[data-option-group]');
        if (this.openGroup === group) {
          this.close();
        } else {
          this.open(group);
        }
      }

      open(group) {
        if (this.openGroup) this.close({ returnFocus: false });

        const trigger = group.querySelector('[data-option-trigger]');
        const panel = group.querySelector('[data-option-panel]');
        if (!trigger || !panel) return;

        trigger.setAttribute('aria-expanded', 'true');
        panel.setAttribute('data-open', '');
        this.openGroup = group;

        /**
         * Focus goes to the SELECTED swatch, so a keyboard user lands on their
         * current choice. After a mouse click the browser does not paint a
         * `focus-visible` ring for programmatic focus, so this is invisible to
         * mouse users.
         */
        const target =
          panel.querySelector('[data-option-value][aria-pressed="true"]') ??
          panel.querySelector('[data-option-value]');
        target?.focus({ preventScroll: true });
      }

      close({ returnFocus = true } = {}) {
        const group = this.openGroup;
        if (!group) return;

        const trigger = group.querySelector('[data-option-trigger]');
        const panel = group.querySelector('[data-option-panel]');
        trigger?.setAttribute('aria-expanded', 'false');
        panel?.removeAttribute('data-open');
        this.openGroup = null;
        this.endPreview();

        if (returnFocus) trigger?.focus({ preventScroll: true });
      }

      handleDocumentPointerDown(event) {
        if (!this.openGroup) return;
        if (this.openGroup.contains(event.target)) return;
        this.close({ returnFocus: false });
      }

      handleDocumentKeydown(event) {
        if (event.key !== 'Escape' || !this.openGroup) return;
        this.close();
      }

      /**
       * Keyboard focus leaving the group closes the pop-up. `relatedTarget` is
       * null for a click on nothing focusable — including the pop-up's own
       * padding — so that case is left to the pointerdown handler, which knows
       * whether the press was inside.
       */
      handleFocusOut(event) {
        if (!this.openGroup) return;
        const next = event.relatedTarget;
        if (!next || this.openGroup.contains(next)) return;
        this.close({ returnFocus: false });
      }

      /** Arrow keys walk the swatches, Home and End jump to the ends. */
      handlePanelKeydown(event) {
        const swatches = Array.from(
          event.currentTarget.querySelectorAll('[data-option-value]'),
        );
        const index = swatches.indexOf(document.activeElement);
        if (index === -1) return;

        let target = null;
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            target = (index + 1) % swatches.length;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            target = (index - 1 + swatches.length) % swatches.length;
            break;
          case 'Home':
            target = 0;
            break;
          case 'End':
            target = swatches.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        swatches[target].focus();
      }

      /* ------------------------------------------------------------------ preview */

      handlePointerOver(event) {
        if (event.pointerType !== 'mouse') return;
        const swatch = event.target.closest('[data-option-value]');
        if (!swatch) return;

        const imageId = swatch.dataset.previewImage;
        clearTimeout(this.hoverTimer);
        if (!imageId) return;

        this.hoverTimer = setTimeout(() => {
          this.previewing = true;
          this.gallery?.showImage?.(imageId);
        }, VariantPicker.HOVER_DELAY);
      }

      handlePointerLeave(event) {
        if (event.pointerType !== 'mouse') return;
        this.endPreview();
      }

      /** Put the gallery back on the selected variant, if a preview moved it. */
      endPreview() {
        clearTimeout(this.hoverTimer);
        if (!this.previewing) return;
        this.previewing = false;
        this.gallery?.showImage?.(this.currentVariant?.image);
      }

      /* ---------------------------------------------------------------- selection */

      handleValueClick(event) {
        const swatch = event.target.closest('[data-option-value]');
        if (!swatch) return;

        const group = swatch.closest('[data-option-group]');
        const position = this.groups.indexOf(group);
        if (position === -1) return;

        clearTimeout(this.hoverTimer);
        this.previewing = false;
        this.selected[position] = swatch.dataset.optionValue;

        this.close();
        this.applySelection(swatch.dataset.optionValue);
      }

      applySelection(chosenValue) {
        const variant = this.currentVariant;

        this.syncValues();

        if (!variant) {
          this.setButtonState('unavailable');
        } else {
          this.idInput.value = variant.id;
          if (this.priceElement) this.priceElement.textContent = variant.price;
          this.setButtonState(variant.available ? 'available' : 'sold_out');
          this.updateUrl(variant.id);
          this.gallery?.showImage?.(variant.image);
        }

        this.announce(chosenValue);
      }

      /**
       * Refresh every swatch against the current selection: which one is
       * pressed, which are sold out, and which photograph each would preview.
       * Also rewrites each option's visible selected-value line and the hidden
       * "selected:" text in its trigger.
       *
       * Availability is judged per combination — "this value, with the other
       * options as currently chosen" — so with Gemstone + Size, a gemstone
       * reads as sold out only in the sizes where it actually is.
       */
      syncValues() {
        this.groups.forEach((group, position) => {
          group.querySelectorAll('[data-option-value]').forEach((swatch) => {
            const value = swatch.dataset.optionValue;
            const selection = this.selected.slice();
            selection[position] = value;
            const candidate = this.findVariant(selection);
            const soldOut = !candidate || !candidate.available;

            swatch.setAttribute('aria-pressed', value === this.selected[position] ? 'true' : 'false');
            swatch.toggleAttribute('data-sold-out', soldOut);
            swatch.dataset.previewImage = candidate?.image ?? '';

            const note = swatch.querySelector('[data-sold-out-note]');
            if (note) note.hidden = !soldOut;
          });

          const selectedName = group.querySelector('[data-option-selected-name]');
          if (selectedName && this.selected[position] != null) {
            selectedName.textContent = this.selected[position];
          }

          const current = group.querySelector('[data-option-current]');
          if (current?.dataset.template && this.selected[position] != null) {
            current.textContent = current.dataset.template.replace(
              '__value__',
              this.selected[position],
            );
          }
        });
      }

      /**
       * Swap the buy button between the filled and outline treatments. The two
       * are separate utilities in assets/css-tailwind.css precisely so they are
       * never layered — see the note on `button-outline` in the section.
       */
      setButtonState(state) {
        const button = this.addButton;
        if (!button) return;

        const available = state === 'available';
        const labels = {
          available: button.dataset.labelAvailable,
          sold_out: button.dataset.labelSoldOut,
          unavailable: button.dataset.labelUnavailable,
        };

        button.disabled = !available;
        button.classList.toggle('button-block', available);
        button.classList.toggle('button-outline', !available);
        if (labels[state]) button.textContent = labels[state];
      }

      /**
       * `replaceState`, not `pushState`: choosing a gemstone is not navigation,
       * and Back should leave the product, not walk through every swatch tried.
       */
      updateUrl(variantId) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variantId);
          window.history.replaceState(window.history.state, '', url.toString());
        } catch (error) {
          console.warn('[variant-picker] could not update the URL', error);
        }
      }

      announce(value) {
        const template = this.statusRegion?.dataset.template;
        if (!template) return;
        this.statusRegion.textContent = template.replace('__value__', value);
      }
    },
  );
}
